import { Workbook, Worksheet } from 'exceljs'
import { VersionCostCalculator } from '../calculators/costs/VersionCostCalculator'
import { CostType } from '../generated/graphql'
import { sanitizeWorksheetName } from '../project-versions/create-version-export'
import { reduceToSumOfItems } from '../util/array-utility'

export type OtherCostValues = {
  costType: CostType
  values: number[]
}

type ColumnData = {
  index: number
  heading?: string
  values: number[]
}

/* eslint-disable no-param-reassign */
const applyStylesToSheet = (sheet: Worksheet) => {
  // Set default font.
  sheet.columns.forEach((column) => {
    column.font = {
      ...column.font,
      name: 'Calibri',
      size: 11,
    }
  })

  // Bold headers on first two rows.
  sheet.getRows(1, 2)?.forEach((row) =>
    row.eachCell((cell) => {
      cell.style.font = {
        ...cell.style.font,
        bold: true,
      }
    }),
  )

  // One decimal for single values.
  sheet.columns.slice(1).forEach((column) => {
    column.numFmt = '#,##0.0'
  })

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  const pixelsPerExcelWidthUnit = 7
  const paddingInPixels = 5

  const estimateCellWidth = (location: string) => {
    const cell = sheet.getCell(location)
    const content = cell.toString()

    if (!context || !cell.font?.size || !cell.font?.name) {
      return content.length
    }

    context.font = `${cell.font.size}pt ${cell.font.name}, sans-serif`

    return (
      (context.measureText(content).width + paddingInPixels) /
      pixelsPerExcelWidthUnit
    )
  }

  // Set first column width.
  sheet.getColumn('A').width = Math.max(
    estimateCellWidth('A1'),
    estimateCellWidth('A2'),
  )

  // Set column widths based on header width.
  sheet.columns.slice(1).forEach((column) => {
    column.width = Math.max(
      estimateCellWidth(`${column.letter ?? 'A'}2`),
      estimateCellWidth(`${column.letter ?? 'A'}${sheet.rowCount}`),
    )
  })
}

const addDataToSheet = (
  sheet: Worksheet,
  version: VersionCostCalculator,
  t: (key: string) => string,
) => {
  const inspectionLengthInYears = version.getInspectionLengthInYears()
  const firstColumn = {
    index: 1,
    heading: t('costs.export.inspectionYear'),
    values: Array.from(Array(inspectionLengthInYears + 1).keys()),
  }

  const energyColumn = {
    index: 2,
    heading: t('common.energy'),
    values: version.getYearlyEnergyValuesForExport(),
  }

  const otherCostColums = version
    .getYearlyOtherValuesForExport()
    .map((valuesByCostType, i) => ({
      index: 3 + i,
      heading: t(`costType.${valuesByCostType.costType}`),
      values: valuesByCostType.values,
    }))

  const totalColumn = {
    index: 3 + otherCostColums.length,
    heading: t('common.total'),
    values: [energyColumn, ...otherCostColums]
      .map((costCategory) => costCategory.values)
      .reduce(reduceToSumOfItems, []),
  }

  const columnData: ColumnData[] = [
    firstColumn,
    energyColumn,
    ...otherCostColums,
    totalColumn,
  ]

  // Insert column data
  columnData.forEach((filter) => {
    // eslint-disable-next-line no-param-reassign
    sheet.getColumn(filter.index).values = filter.values
  })

  const headerRows = [[], columnData.map((filter) => filter.heading)]
  sheet.insertRows(1, headerRows)
}

export function createCostExport(
  t: (key: string) => string,
  versionCalculators: VersionCostCalculator[],
): Workbook {
  const workbook = new Workbook()
  workbook.creator = 'Sweco ENEX'
  workbook.created = new Date()

  const addWorksheet = (version: VersionCostCalculator) => {
    const name = sanitizeWorksheetName(version.getName())
    const worksheet = workbook.addWorksheet(name)

    addDataToSheet(worksheet, version, t)

    const nameCell = worksheet.getCell('A1')
    nameCell.value = name

    applyStylesToSheet(worksheet)
  }

  versionCalculators.forEach((version) => addWorksheet(version))
  return workbook
}
